import { useEffect, useRef } from 'react';
import { postVoiceEvent } from '../../api';
import { getHeadersWithUniqueVisitorId } from '../../../Auth';

/**
 * `useWebRtcStats` (Plan 3b) — sample WebRTC quality every 5 s while the
 * `RTCPeerConnection` is connected, and ship the result through
 * `/voice/event` as a `{ type: 'webrtc.stats', payload }` browser event.
 *
 * The events feed `VoiceObservabilityService.finalizeSession`, which
 * aggregates them into `avgRttMs` / `maxJitterMs` / `packetLossPctIn`/`Out`
 * on the session row. Sampling stops automatically when the connection
 * leaves the connected state.
 */
export interface UseWebRtcStatsParams {
  conversationId: string;
  pc?: RTCPeerConnection | null;
  enabled: boolean;
  intervalMs?: number;
}

const DEFAULT_INTERVAL_MS = 5_000;

interface SnapshotRow {
  packetsLost?: number;
  packetsReceived?: number;
  jitterSec?: number;
  bytes?: number;
  audioLevel?: number;
  ts: number;
}

/** Per-direction stats accumulator for delta computation across samples. */
interface DirectionAcc {
  lastPacketsLost: number;
  lastPacketsReceived: number;
  lastBytes: number;
  lastTs: number;
}

export function useWebRtcStats(params: UseWebRtcStatsParams): void {
  const { conversationId, pc, enabled, intervalMs = DEFAULT_INTERVAL_MS } = params;

  /** Cancellation guard owned by the current effect run. */
  const cancelledRef = useRef<{ flag: boolean }>({ flag: false });
  /** Cached auth headers (Plan 4c fix) so stats events match the call's owner. */
  const authHeadersRef = useRef<Record<string, string>>({});

  useEffect(() => {
    if (!enabled || !conversationId || !pc) return;

    const cancelled = { flag: false };
    cancelledRef.current = cancelled;

    // Warm auth headers up-front so the first sample carries them.
    getHeadersWithUniqueVisitorId()
      .then((h) => {
        if (!cancelled.flag) authHeadersRef.current = h;
      })
      .catch(() => {
        // ignore — fire-and-forget stats can fall back to empty headers
      });
    const inbound: DirectionAcc = { lastPacketsLost: 0, lastPacketsReceived: 0, lastBytes: 0, lastTs: 0 };
    const outbound: DirectionAcc = { lastPacketsLost: 0, lastPacketsReceived: 0, lastBytes: 0, lastTs: 0 };

    const sample = async () => {
      if (cancelled.flag) return;
      // Skip while the connection isn't fully up — peers like Safari may
      // briefly enter `connected` then bounce; we only want stats from a
      // stable connection.
      const connState = pc.connectionState;
      if (connState !== 'connected') return;

      let report: RTCStatsReport;
      try {
        report = await pc.getStats();
      } catch {
        return;
      }
      if (cancelled.flag) return;

      const inboundRow = pickAudioRow(report, 'inbound-rtp');
      const outboundRow = pickAudioRow(report, 'outbound-rtp');
      const candidatePair = pickActiveCandidatePair(report);
      const remoteInboundRow = pickRemoteInbound(report);

      const nowMs = Date.now();

      const computeRate = (acc: DirectionAcc, row: SnapshotRow | null) => {
        if (!row || row.bytes === undefined) return undefined;
        if (acc.lastTs === 0) {
          acc.lastBytes = row.bytes;
          acc.lastTs = row.ts;
          return undefined;
        }
        const dtSec = Math.max(0.001, (row.ts - acc.lastTs) / 1000);
        const dBytes = Math.max(0, row.bytes - acc.lastBytes);
        acc.lastBytes = row.bytes;
        acc.lastTs = row.ts;
        // bits per second
        return Math.round((dBytes * 8) / dtSec);
      };

      const computeDeltas = (acc: DirectionAcc, row: SnapshotRow | null) => {
        if (!row) return { dLost: 0, dRecv: 0 };
        const dLost = Math.max(0, (row.packetsLost ?? 0) - acc.lastPacketsLost);
        const dRecv = Math.max(0, (row.packetsReceived ?? 0) - acc.lastPacketsReceived);
        acc.lastPacketsLost = row.packetsLost ?? acc.lastPacketsLost;
        acc.lastPacketsReceived = row.packetsReceived ?? acc.lastPacketsReceived;
        return { dLost, dRecv };
      };

      const bitrateIn = computeRate(inbound, inboundRow);
      const bitrateOut = computeRate(outbound, outboundRow);
      const inDeltas = computeDeltas(inbound, inboundRow);
      const outDeltas = computeDeltas(outbound, outboundRow);

      const payload = {
        rttMs:
          candidatePair && typeof candidatePair.currentRoundTripTimeSec === 'number'
            ? Math.round(candidatePair.currentRoundTripTimeSec * 1000)
            : remoteInboundRow && typeof remoteInboundRow.roundTripTimeSec === 'number'
            ? Math.round(remoteInboundRow.roundTripTimeSec * 1000)
            : undefined,
        jitterMs:
          inboundRow?.jitterSec !== undefined ? Math.round(inboundRow.jitterSec * 1000) : undefined,
        packetsLostIn: inDeltas.dLost,
        packetsReceivedIn: inDeltas.dRecv,
        packetsLostOut: outDeltas.dLost,
        packetsReceivedOut: outDeltas.dRecv,
        bitrateIn,
        bitrateOut,
        audioLevelIn: inboundRow?.audioLevel,
        audioLevelOut: outboundRow?.audioLevel,
      };

      postVoiceEvent({
        conversationId,
        events: [{ type: 'webrtc.stats', payload, t: nowMs }],
        headersToInject: authHeadersRef.current,
      });
    };

    // Run a sample immediately (no point waiting 5s for the first datapoint),
    // then on the regular interval.
    void sample();
    const handle = setInterval(() => {
      void sample();
    }, intervalMs);

    return () => {
      cancelled.flag = true;
      clearInterval(handle);
    };
  }, [enabled, conversationId, pc, intervalMs]);
}

/**
 * Find the audio row of the requested RTP direction. WebRTC stats reports
 * may include video too; we only ship audio metrics.
 */
function pickAudioRow(report: RTCStatsReport, direction: 'inbound-rtp' | 'outbound-rtp'): SnapshotRow | null {
  let row: SnapshotRow | null = null;
  report.forEach((s: any) => {
    if (s.type !== direction) return;
    if (s.kind && s.kind !== 'audio') return;
    if (s.mediaType && s.mediaType !== 'audio') return;
    row = {
      packetsLost: typeof s.packetsLost === 'number' ? s.packetsLost : undefined,
      packetsReceived: typeof s.packetsReceived === 'number' ? s.packetsReceived : undefined,
      jitterSec: typeof s.jitter === 'number' ? s.jitter : undefined,
      bytes:
        typeof s.bytesReceived === 'number'
          ? s.bytesReceived
          : typeof s.bytesSent === 'number'
          ? s.bytesSent
          : undefined,
      audioLevel: typeof s.audioLevel === 'number' ? s.audioLevel : undefined,
      ts: typeof s.timestamp === 'number' ? s.timestamp : Date.now(),
    };
  });
  return row;
}

/** Active ICE candidate pair carries the most reliable RTT signal. */
function pickActiveCandidatePair(
  report: RTCStatsReport,
): { currentRoundTripTimeSec?: number } | null {
  let pair: any = null;
  report.forEach((s: any) => {
    if (s.type !== 'candidate-pair') return;
    if (!s.nominated && pair) return;
    if (s.state && s.state !== 'succeeded') return;
    pair = s;
  });
  if (!pair) return null;
  return { currentRoundTripTimeSec: pair.currentRoundTripTime };
}

/** `remote-inbound-rtp` carries RTT from the remote peer's perspective. */
function pickRemoteInbound(report: RTCStatsReport): { roundTripTimeSec?: number } | null {
  let row: any = null;
  report.forEach((s: any) => {
    if (s.type !== 'remote-inbound-rtp') return;
    if (s.kind && s.kind !== 'audio') return;
    row = s;
  });
  if (!row) return null;
  return { roundTripTimeSec: row.roundTripTime };
}
